import { PENDING_SHORTCUTS, appendPending } from '@sales-rules';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { errorMessage } from '../../lib/api';
import {
  dmy,
  salesApi,
  statusLabel,
  type ImportPreview,
  type ImportResult,
  type SalesProposal,
  type SalesTrashItem,
  type SellerRef,
} from '../../lib/sales';

/** Janela centralizada (pendência, importar, lixeira). Fecha com Esc e clicando fora. */
function CenterModal({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="scrim center"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      {children}
    </div>
  );
}

/** Mover para Pendente exige o texto; os atalhos somam com " · ". */
export function PendingDialog({
  proposal,
  onClose,
  onConfirm,
}: {
  proposal: SalesProposal;
  onClose: () => void;
  onConfirm: (text: string) => Promise<void>;
}) {
  const [text, setText] = useState(proposal.pendingText ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!text.trim()) {
      setError('Escreva qual é a pendência.');
      return;
    }
    setSaving(true);
    try {
      await onConfirm(text.trim());
    } catch (saveError) {
      setError(errorMessage(saveError, 'Não consegui salvar agora.'));
      setSaving(false);
    }
  };

  return (
    <CenterModal onClose={onClose}>
      <form className="pm" noValidate onSubmit={submit}>
        <h3>Qual a pendência?</h3>
        <div className="muted">
          {proposal.requestNumber ? `${proposal.requestNumber} · ` : ''}
          {proposal.company}
        </div>
        <div className="pmchips">
          {PENDING_SHORTCUTS.map((shortcut) => (
            <button
              key={shortcut}
              type="button"
              className="act"
              onClick={() => {
                setText((current) => appendPending(current, shortcut));
                input.current?.focus();
              }}
            >
              {shortcut}
            </button>
          ))}
        </div>
        <input
          ref={input}
          type="text"
          placeholder="Ex.: falta RG do dependente"
          autoComplete="off"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        {error && <div className="err">{error}</div>}
        <div className="factions">
          <span style={{ flex: 1 }} />
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn" type="submit" disabled={saving}>
            Mover para Pendente
          </button>
        </div>
      </form>
    </CenterModal>
  );
}

type Source =
  { kind: 'sheet'; file: File } | { kind: 'artifact'; name: string; items: unknown[] };

/**
 * Importar a planilha (.xlsx) ou o JSON do artefato. Primeiro mostra a prévia; o gestor
 * escolhe a conta de cada nome da coluna VENDEDOR. Vendedor importa tudo para si.
 */
export function ImportDialog({
  initialFile,
  isManager,
  sellers,
  onClose,
  onDone,
}: {
  initialFile: File | null;
  isManager: boolean;
  sellers: SellerRef[];
  onClose: () => void;
  onDone: (result: ImportResult) => void;
}) {
  const [file, setFile] = useState<File | null>(initialFile);
  const [map, setMap] = useState<Record<string, string> | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // A prévia é uma leitura: refaz quando o arquivo muda (inclusive o arrastado para a tela).
  const read = useQuery({
    queryKey: ['sales', 'import-preview', file?.name, file?.size, file?.lastModified],
    enabled: Boolean(file),
    retry: false,
    gcTime: 0,
    queryFn: async (): Promise<{ source: Source; preview: ImportPreview }> => {
      const chosen = file as File;
      if (/\.json$/i.test(chosen.name)) {
        let parsed: unknown;
        try {
          parsed = JSON.parse(await chosen.text());
        } catch {
          throw new Error('Esse arquivo .json não está no formato do Minhas Vendas.');
        }
        const items = Array.isArray(parsed)
          ? parsed
          : ((parsed as { propostas?: unknown[] }).propostas ??
            (parsed as { proposals?: unknown[] }).proposals ??
            []);
        return {
          source: { kind: 'artifact', name: chosen.name, items },
          preview: await salesApi.previewArtifact(items),
        };
      }
      return {
        source: { kind: 'sheet', file: chosen },
        preview: await salesApi.previewSheet(chosen),
      };
    },
  });
  const preview = read.data?.preview ?? null;
  const suggested = Object.fromEntries(
    (preview?.sellers ?? []).map((item) => [item.name, item.suggestion ?? '']),
  );
  const chosenMap = map ?? suggested;

  const importing = useMutation({
    mutationFn: () => {
      const source = read.data?.source as Source;
      const sellerMap = isManager
        ? Object.fromEntries(Object.entries(chosenMap).map(([name, id]) => [name, id || null]))
        : undefined;
      return source.kind === 'sheet'
        ? salesApi.importSheet(source.file, sellerMap)
        : salesApi.importArtifact(source.items, sellerMap);
    },
    onSuccess: onDone,
  });
  const busy = read.isFetching || importing.isPending;
  const error = read.error
    ? read.error instanceof Error && !('isAxiosError' in read.error)
      ? read.error.message
      : errorMessage(
          read.error,
          'Não consegui ler esse arquivo. Salve como .xlsx e tente de novo.',
        )
    : importing.error
      ? errorMessage(importing.error, 'Não consegui importar agora.')
      : null;

  return (
    <CenterModal onClose={onClose}>
      <div className="pm" style={{ width: 'min(560px, 100%)' }}>
        <h3>Importar propostas</h3>
        <div className="muted">
          A planilha (.xlsx, aba CADASTRO) ou o arquivo .json exportado do Minhas Vendas.
          Reimportar atualiza sem duplicar, e o status só avança.
        </div>
        <div>
          <button
            className="btn ghost"
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={busy}
          >
            {file ? 'Escolher outro arquivo' : 'Escolher arquivo'}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.json"
            hidden
            onChange={(event) => {
              const chosen = event.target.files?.[0];
              if (chosen) {
                setFile(chosen);
                setMap(null);
              }
              event.target.value = '';
            }}
          />
        </div>
        {read.isFetching && (
          <div className="pdfbar">
            <span />
          </div>
        )}
        {preview && (
          <div className="pdfst">
            {preview.sheet && (
              <>
                Aba <b>{preview.sheet}</b>:{' '}
              </>
            )}
            <b>{preview.proposals}</b> {preview.proposals === 1 ? 'proposta' : 'propostas'},{' '}
            <b>{preview.beneficiaries}</b> beneficiários.
            {preview.skipped.length > 0 && (
              <div className="warn">
                {preview.skipped.length}{' '}
                {preview.skipped.length === 1 ? 'linha fica' : 'linhas ficam'} de fora:{' '}
                {preview.skipped
                  .slice(0, 6)
                  .map((item) => `linha ${item.row} (${item.reason})`)
                  .join(', ')}
                {preview.skipped.length > 6 ? '…' : ''}
              </div>
            )}
            {!isManager && <div>Todas entram como suas.</div>}
          </div>
        )}
        {preview?.sellers && preview.sellers.length > 0 && (
          <div className="field">
            <span>De quem é cada nome da coluna Vendedor?</span>
            <div className="brows">
              {preview.sellers.map((item) => (
                <label
                  key={item.name}
                  className="brow"
                  style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}
                >
                  <span>
                    <b>{item.name || '(em branco)'}</b>{' '}
                    <span className="muted">
                      · {item.proposals} {item.proposals === 1 ? 'proposta' : 'propostas'}
                    </span>
                  </span>
                  <select
                    value={chosenMap[item.name] ?? ''}
                    onChange={(event) =>
                      setMap({ ...chosenMap, [item.name]: event.target.value })
                    }
                  >
                    <option value="">Sem conta (só o gestor vê)</option>
                    {sellers.map((seller) => (
                      <option key={seller.membershipId} value={seller.membershipId}>
                        {seller.name}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </div>
        )}
        {error && <div className="err">{error}</div>}
        <div className="factions">
          <span style={{ flex: 1 }} />
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn"
            type="button"
            disabled={!preview || busy || preview.proposals === 0}
            onClick={() => importing.mutate()}
          >
            {importing.isPending ? 'Importando…' : 'Importar'}
          </button>
        </div>
      </div>
    </CenterModal>
  );
}

/** Lixeira de vendas: só as próprias (o gestor vê todas), 30 dias. */
export function TrashDialog({
  onClose,
  onChanged,
  toast,
}: {
  onClose: () => void;
  onChanged: () => void;
  toast: (message: string) => void;
}) {
  const trash = useQuery({ queryKey: ['sales', 'trash'], queryFn: salesApi.trash });
  const items: SalesTrashItem[] | null = trash.data ?? null;
  const [actionError, setError] = useState<string | null>(null);
  const error =
    actionError ??
    (trash.error ? errorMessage(trash.error, 'Não consegui abrir a lixeira.') : null);
  const [armed, setArmed] = useState<string | null>(null);
  const reload = () => trash.refetch();

  const restore = async (item: SalesTrashItem) => {
    try {
      await salesApi.restore(item.id);
      toast(`${item.requestNumber ?? item.company} voltou para o quadro`);
      onChanged();
      await reload();
    } catch (restoreError) {
      setError(errorMessage(restoreError, 'Não consegui restaurar.'));
    }
  };

  const purge = async (item: SalesTrashItem) => {
    if (armed !== item.id) {
      setArmed(item.id);
      window.setTimeout(
        () => setArmed((current) => (current === item.id ? null : current)),
        4000,
      );
      return;
    }
    try {
      await salesApi.purge(item.id);
      await reload();
    } catch (purgeError) {
      setError(errorMessage(purgeError, 'Não consegui apagar.'));
    }
  };

  return (
    <CenterModal onClose={onClose}>
      <div className="pm" style={{ width: 'min(560px, 100%)' }}>
        <h3>Lixeira</h3>
        <div className="muted">Propostas excluídas ficam aqui por 30 dias.</div>
        {items === null && !error && (
          <div className="pdfbar">
            <span />
          </div>
        )}
        {items?.length === 0 && <div className="empty">Nada na lixeira.</div>}
        <div className="brows">
          {items?.map((item) => (
            <div
              key={item.id}
              className="entrow"
              style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}
            >
              <div className="what">
                <b>{item.company}</b>
                <span className="muted" style={{ fontSize: '.85rem' }}>
                  {item.requestNumber ? `${item.requestNumber} · ` : ''}
                  {statusLabel(item.status)} · vigência {dmy(item.effectiveDate)} · apaga em{' '}
                  {new Date(item.purgeAfter).toLocaleDateString('pt-BR')}
                  {item.seller ? ` · ${item.seller.name}` : ''}
                </span>
              </div>
              <div className="btns">
                <button className="act" type="button" onClick={() => void restore(item)}>
                  Restaurar
                </button>
                <button className="act" type="button" onClick={() => void purge(item)}>
                  {armed === item.id ? 'Toque de novo' : 'Apagar'}
                </button>
              </div>
            </div>
          ))}
        </div>
        {error && <div className="err">{error}</div>}
        <div className="factions">
          <span style={{ flex: 1 }} />
          <button className="btn ghost" type="button" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </CenterModal>
  );
}

/** Filtro de múltipla escolha (meses e dias de vigência), como o do artefato. */
export function MultiSelect({
  options,
  selected,
  allLabel,
  small,
  format,
  onChange,
}: {
  options: Array<{ value: string; label: string; count?: number }>;
  selected: Set<string>;
  allLabel: string;
  small?: boolean;
  format: (value: string) => string;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const label = !selected.size
    ? allLabel
    : selected.size <= 2
      ? [...selected].sort().map(format).join(', ')
      : `${selected.size} selecionados`;

  return (
    <div ref={box} className={`ms${small ? ' small' : ''}${selected.size ? ' on' : ''}`}>
      <button
        type="button"
        className="ms-btn"
        aria-haspopup="true"
        onClick={() => setOpen(!open)}
      >
        {label}
      </button>
      {open && (
        <div className="ms-pop">
          <div className="ms-list">
            {options.map((option) => (
              <label key={option.value}>
                <input
                  type="checkbox"
                  checked={selected.has(option.value)}
                  onChange={(event) => {
                    const next = new Set(selected);
                    if (event.target.checked) next.add(option.value);
                    else next.delete(option.value);
                    onChange(next);
                  }}
                />
                <span>{option.label}</span>
                {option.count !== undefined && <span className="cnt">{option.count}</span>}
              </label>
            ))}
          </div>
          <div className="ms-foot">
            <button type="button" className="linkbtn" onClick={() => onChange(new Set())}>
              Limpar
            </button>
            <button type="button" className="btn" onClick={() => setOpen(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
